import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useBridgeAPI, useBridgeState } from '@digitaz/bridge-sdk-client';
import Link from 'next/link';
import {
  setAdriverSelectedCity,
  setCurrentState,
  setIsBannedUser,
  setReplenishmentNotifications,
  toggleReplenishmentNotificationsModal
} from '../../actions';
import { Tooltip } from '../ui/Tooltip';
import HamburgerMenu from '../HamburgerMenu';
import Icon from '../Icon';
import WhitePlusInCircle from '../../public/static/images/icon-plus-in-circle--white.svg';
import Plus from '../../public/static/images/green-plus.svg';
import CloseWebview from '../../public/static/images/close-webview.svg';
import useScreenSize from '../../hooks/useScreenSize';
import useBugsnagNotifier from '../../hooks/useBugsnagNotifier';
import { getIsCurrentPage } from '../../utils/helpers/getIsCurrentPage';
import {
  ButtonsGroupContainer,
  FlexedHeader,
  MenuItem,
  MenuItemsContainer,
  BookmarkIconLink,
  WebViewButton,
  Wrapper,
  CreateItemIconLink
} from './styled';
import {
  Cities,
  BUILDING_TYPE_ID,
  SEARCH_RESULT_ROUTES,
  urlHashes
} from '../../utils/helpers/constants';
import { selectSearchFormCurrentState } from '../../store/selectors';
import { companyNameQuery } from '../../api/queries';
import { CompanyNameQuery } from '../../graphql/graphql';
import { ErrorPopup } from '../ErrorPopup';
import { ButtonLocation } from '../../types';
import { setButtonNewAdEvent } from '../../utils/pushDataLayerEvents';
import LoginButton from '../LoginButton';

export interface FlexibleHeaderProps {
  isShowCloseButton: boolean;
  isFixedHeader: boolean;
  isAuthInProgress: boolean;
}

export const FlexibleHeader = ({
  isShowCloseButton,
  isFixedHeader,
  isAuthInProgress
}: FlexibleHeaderProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const storedFormState = useSelector(selectSearchFormCurrentState);
  const { isWebView } = useBridgeState();
  const { closeWebview } = useBridgeAPI();
  const { isSmall, isMedium, isLarge, isSmallOrLargeDesktop } = useScreenSize();
  const [isBannedModalVisible, setIsBannedModalVisible] = useState(false);

  const [loadCompanyName, { data, error }] = useLazyQuery<CompanyNameQuery>(
    companyNameQuery,
    {
      ssr: false
    }
  );
  const isAuth = Boolean(data && data?.profile);
  useBugsnagNotifier(error);

  useEffect(() => {
    dispatch(
      setIsBannedUser(data?.profile ? data.profile.isRestricted : false)
    );
  }, [data?.profile]);

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    if (data?.profile?.isRestricted) {
      setIsBannedModalVisible(true);
      e.preventDefault();
    } else if (router.pathname === path) {
      e.preventDefault();
      router.reload();
    }
  };

  useEffect(() => {
    if (data?.profile?.notifications && data?.profile?.notifications[0]) {
      dispatch(setReplenishmentNotifications(data.profile.notifications));
      dispatch(toggleReplenishmentNotificationsModal(true));
    }
  }, [data, dispatch]);

  const menuItemsList = [
    {
      title: t('main_menu.sale'),
      path: SEARCH_RESULT_ROUTES.SALE,
      isShow: !isSmall,
      cypress: 'sale',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'false',
            category_id: BUILDING_TYPE_ID.FLAT_NEW
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('main_menu.lease'),
      path: SEARCH_RESULT_ROUTES.RENT,
      isShow: !isSmall,
      cypress: 'lease',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'true',
            category_id: BUILDING_TYPE_ID.FLAT_NEW
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('search.form.paid_daily'),
      path: SEARCH_RESULT_ROUTES.RENT_DAILY,
      isShow: !isSmall,
      cypress: 'paid-daily',
      onClick: () => {
        dispatch(
          setCurrentState({
            ...storedFormState,
            city_id: Cities.Baku.id,
            leased: 'true',
            category_id: BUILDING_TYPE_ID.FLAT_NEW,
            paid_daily: ['true']
          })
        );
        dispatch(setAdriverSelectedCity(Cities.Baku.az));
      }
    },
    {
      title: t('main_menu.agencies'),
      path: '/agentlikler',
      isShow: isSmallOrLargeDesktop,
      cypress: 'agencies'
    },
    {
      title: t('main_menu.residences'),
      path: '/yasayis-kompleksleri',
      isShow: isSmallOrLargeDesktop,
      cypress: 'residences'
    }
  ];

  const handleClickOnAddNewItem = (e, buttonLocation: ButtonLocation) => {
    setButtonNewAdEvent(buttonLocation);
    handleLinkClick(e, '/items/new');
  };

  const buttonsGroupList = [
    {
      button: (
        <Tooltip
          text={t('pages.bookmarks.title')}
          wrapperHeight={30}
          wrapperWidth={30}
          tooltipTopSpace={45}
        >
          <BookmarkIconLink
            data-cy="favorites-icon-btn"
            href="/bookmarks"
            data-stat="header-btn"
            aria-label={t('pages.bookmarks.title')}
          />
        </Tooltip>
      ),
      isShow: !isSmall
    },
    {
      button: (
        <CreateItemIconLink
          href="/items/new"
          data-cy="header-add-new-item-btn"
          data-stat="header-add-new-item-btn"
          onClick={e => handleClickOnAddNewItem(e, ButtonLocation.DeskHeader)}
        >
          <WhitePlusInCircle />
          {t('common.create_new_item')}
        </CreateItemIconLink>
      ),
      isShow: isLarge
    },
    {
      button: (
        <Link
          href="/items/new"
          data-cy="add-new-icon-btn"
          data-stat="header-btn"
          role="button"
          onClick={e => handleClickOnAddNewItem(e, ButtonLocation.MobileHeader)}
          aria-label={t('common.create_new_item')}
        >
          <Icon indent={false} IconComponent={Plus} width={30} height={30} />
        </Link>
      ),
      isShow: (isSmall && !isWebView) || isMedium
    },
    {
      button: <LoginButton {...{ isAuth }} profileData={data?.profile} />,
      isShow: !isSmall
    },
    {
      button: (
        <WebViewButton onClick={closeWebview}>
          <i>
            <Icon
              IconComponent={CloseWebview}
              width={15}
              height={15}
              indent={false}
              color={'#a9a39e'}
            />
          </i>
        </WebViewButton>
      ),
      isShow: isSmall && isWebView && isShowCloseButton
    }
  ];

  useEffect(() => {
    if (!isAuthInProgress) loadCompanyName();
  }, [isAuthInProgress]);

  const hasAuthOrMoreHash =
    router.asPath.includes(urlHashes.authentication) ||
    router.asPath.includes(urlHashes.more);

  return (
    <FlexedHeader data-cy="header" id="header" {...{ isFixedHeader }}>
      <Wrapper $hasAuthOrMoreHash={hasAuthOrMoreHash}>
        <HamburgerMenu />
        <MenuItemsContainer data-cy="header-items">
          {menuItemsList.map(({ title, path, isShow, cypress, onClick }) => {
            if (!isShow) return null;

            const isCurrentPage = getIsCurrentPage(path, router);

            return (
              <MenuItem
                href={path}
                key={title}
                data-cy={cypress}
                onClick={onClick}
                {...{ isCurrentPage }}
              >
                {title}
              </MenuItem>
            );
          })}
        </MenuItemsContainer>
        <ButtonsGroupContainer>
          {buttonsGroupList.map(({ button, isShow }, index) => {
            if (!isShow) return null;

            return <React.Fragment key={index}>{button}</React.Fragment>;
          })}
        </ButtonsGroupContainer>
      </Wrapper>
      <ErrorPopup
        isOpen={isBannedModalVisible}
        onClose={() => setIsBannedModalVisible(false)}
        isBannedPhone
      />
    </FlexedHeader>
  );
};
